"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import { SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { formatName } from "@/lib/format-data";
import { CandidatesResponse, PatientClaimAuthMode } from "@/types/patient-claim";
import { SelfSchedulePatient } from "@/types/self-schedule";
import { listPatientCandidates } from "@/api/auth/patient-claim";
import { authService } from "@/services/auth-service";
import { RECENT_CLAIM_TTL_MS, useRecentPatientClaimStore } from "@/stores/recent-patient-claim-store";
import { useTenantStore } from "@/stores/tenant-store";
function currentTenantSlug(): string | null {
  if (typeof window === "undefined") return null;
  return window.location.pathname.split("/").filter(Boolean)[0] ?? null;
}

/**
 * Candidate lookup is tenant-scoped by the API's TENANT header. Keep that
 * scope in the cache key too so a same-UID navigation between clinics cannot
 * reuse another clinic's Patient response.
 */
export const patientClaimQueryKey = (firebaseUid: string | null, tenantSlug = currentTenantSlug()) => ["patient-claim", tenantSlug, firebaseUid] as const;

/**
 * Seed a portal claim from a successful link mutation.
 *
 * Claim probes can be in flight while Firebase switches from the phone user
 * back to the Google user. Cancelling before writing the mutation response is
 * important: an older NONE response must not arrive after this write and
 * replace the authoritative patient with an empty claim.
 */
export async function primePatientClaim(queryClient: QueryClient, firebaseUid: string, patient: SelfSchedulePatient): Promise<void> {
  const queryKey = patientClaimQueryKey(firebaseUid);
  await queryClient.cancelQueries({
    queryKey,
    exact: true
  });
  queryClient.setQueryData<CandidatesResponse>(queryKey, {
    authMode: PatientClaimAuthMode.PORTAL,
    patient
  });
}
function useFirebaseUid() {
  const $ = _c(6);
  const [firebaseUid, setFirebaseUid] = useState(authService.getCurrentUser()?.uid ?? null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = authService.isAuthInitialized();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [authResolved, setAuthResolved] = useState(t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => authService.addAuthStateListener(user => {
      setFirebaseUid(user?.uid ?? null);
      setAuthResolved(true);
    });
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== authResolved || $[4] !== firebaseUid) {
    t3 = {
      firebaseUid,
      authResolved
    };
    $[3] = authResolved;
    $[4] = firebaseUid;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/**
 * Source of truth for the patient-claim lookup.
 *
 * Tracks the current Firebase user and queries `/auth/patient/candidates`
 * whenever the UID changes. Returns `candidates` (Patients sharing the
 * verified phone in this tenant) and `claimedPatient` (the one this UID has
 * already claimed via /auth/patient/claim, if any). PostHog identity is
 * synced as a side effect when a claimed patient is present.
 */
export function usePatientClaim() {
  const $ = _c(24);
  const {
    firebaseUid,
    authResolved
  } = useFirebaseUid();
  let t0;
  if ($[0] !== firebaseUid) {
    t0 = patientClaimQueryKey(firebaseUid);
    $[0] = firebaseUid;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!firebaseUid;
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: _temp,
      enabled: t1
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const query = useQuery(t2);
  const {
    currentTenant
  } = useTenantStore();
  const data = firebaseUid ? query.data : null;
  const isPortal = data?.authMode === PatientClaimAuthMode.PORTAL;
  let t3;
  if ($[5] !== data) {
    t3 = data?.authMode === PatientClaimAuthMode.PHONE ? data.candidates : [];
    $[5] = data;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const candidates = t3;
  const recentPatientId = useRecentPatientClaimStore(_temp2);
  const recentClaimedAt = useRecentPatientClaimStore(_temp3);
  const isRecentFresh = !!recentClaimedAt && Date.now() - recentClaimedAt < RECENT_CLAIM_TTL_MS;
  const phoneClaimedId = data?.authMode === PatientClaimAuthMode.PHONE ? data.claimedPatientId : null;
  const fastPathPatientId = isRecentFresh && recentPatientId && recentPatientId === phoneClaimedId ? recentPatientId : null;
  let t4;
  if ($[7] !== candidates || $[8] !== data || $[9] !== fastPathPatientId || $[10] !== isPortal) {
    t4 = isPortal ? data.patient : fastPathPatientId ? candidates.find(p => p.id === fastPathPatientId) ?? null : null;
    $[7] = candidates;
    $[8] = data;
    $[9] = fastPathPatientId;
    $[10] = isPortal;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const claimedPatient = t4;
  const lastPatientIdRef = useRef(null);
  let t5;
  let t6;
  if ($[12] !== claimedPatient || $[13] !== currentTenant) {
    t5 = () => {
      if (claimedPatient && claimedPatient.id !== lastPatientIdRef.current) {
        posthog.identify(claimedPatient.id);
        posthog.setPersonProperties({
          tenant: currentTenant ?? "",
          firstName: claimedPatient.firstName,
          lastName: claimedPatient.lastName,
          phoneNumber: claimedPatient.phoneNumber,
          email: claimedPatient.email,
          name: formatName(claimedPatient.firstName, claimedPatient.lastName),
          selfSchedulePatient: true,
          patientStatus: claimedPatient.patientStatus
        });
      }
      lastPatientIdRef.current = claimedPatient?.id ?? null;
    };
    t6 = [claimedPatient, currentTenant];
    $[12] = claimedPatient;
    $[13] = currentTenant;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  const isResolving = !authResolved;
  const isResolvingClaim = !!firebaseUid && isRecentFresh && query.isPending && !claimedPatient;
  const t7 = data?.authMode ?? PatientClaimAuthMode.NONE;
  let t8;
  if ($[16] !== candidates || $[17] !== claimedPatient || $[18] !== firebaseUid || $[19] !== isResolving || $[20] !== isResolvingClaim || $[21] !== query.isPending || $[22] !== t7) {
    t8 = {
      firebaseUid,
      candidates,
      claimedPatient,
      authMode: t7,
      isPending: query.isPending,
      isResolving,
      isResolvingClaim
    };
    $[16] = candidates;
    $[17] = claimedPatient;
    $[18] = firebaseUid;
    $[19] = isResolving;
    $[20] = isResolvingClaim;
    $[21] = query.isPending;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}

/**
 * Mount-only wrapper so the hook can be dropped into a layout without
 * dedicating a component to it.
 */
function _temp3(s_0) {
  return s_0.claimedAt;
}
function _temp2(s) {
  return s.patientId;
}
function _temp(t0) {
  const {
    signal
  } = t0;
  return listPatientCandidates(signal);
}
export function PatientClaimProbe() {
  usePatientClaim();
  return null;
}

/**
 * Local cache patcher for the claimed patient — does not hit the server.
 * Use after operations that update derived fields of the claimed Patient
 * (e.g. appending to `futureAppointments` after booking) so subsequent
 * reads through `usePatientClaim` see the new state without a roundtrip.
 *
 * For *picking* a candidate, use `useClaimPatient` instead — that one
 * actually stamps `firebase_uid` on the chosen Patient row.
 */
export function useUpdateClaimedPatient() {
  const $ = _c(3);
  const queryClient = useQueryClient();
  const {
    firebaseUid
  } = useFirebaseUid();
  let t0;
  if ($[0] !== firebaseUid || $[1] !== queryClient) {
    t0 = action => {
      queryClient.setQueryData(patientClaimQueryKey(firebaseUid), prev => {
        if (!prev) {
          return prev;
        }
        if (prev.authMode === PatientClaimAuthMode.NONE) {
          return prev;
        }
        if (prev.authMode === PatientClaimAuthMode.PORTAL) {
          const next = typeof action === "function" ? action(prev.patient) : action;
          if (!next) {
            return null;
          }
          return {
            authMode: PatientClaimAuthMode.PORTAL,
            patient: next
          };
        }
        const prior = prev.claimedPatientId ? prev.candidates.find(p => p.id === prev.claimedPatientId) ?? null : null;
        const next_0 = typeof action === "function" ? action(prior) : action;
        if (!next_0) {
          return {
            authMode: PatientClaimAuthMode.PHONE,
            candidates: prev.candidates,
            claimedPatientId: null
          };
        }
        const merged = prev.candidates.some(p_1 => p_1.id === next_0.id) ? prev.candidates.map(p_0 => p_0.id === next_0.id ? next_0 : p_0) : [...prev.candidates, next_0];
        return {
          authMode: PatientClaimAuthMode.PHONE,
          candidates: merged,
          claimedPatientId: next_0.id
        };
      });
    };
    $[0] = firebaseUid;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/**
 * Clear the patient-claim cache. Use at sign-out.
 */
export function useResetPatientClaim() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = () => {
      queryClient.removeQueries({
        queryKey: ["patient-claim"]
      });
      useRecentPatientClaimStore.getState().clearRecentPatientId();
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}